// frontend/src/pages/ContactPage.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, Send } from "lucide-react";

export default function ContactPage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (typeof window !== "undefined" && window.location.hostname === "localhost"
      ? "http://localhost:8001"
      : "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");

    try {
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSent(true);
        setFormData({ name: '', email: '', message: '' });
      } else {
        setErrorMsg(data.detail || "Erro ao enviar a mensagem. Verifique seus dados.");
      }
    } catch (error) {
      setErrorMsg("Falha na comunicação com o servidor. Tente novamente mais tarde.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans">
      <header className="border-b bg-white sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/marketing/logo_official.png" className="h-14" alt="OdontoAssist AI Logo" />
          </Link>
          <Link to="/" className="text-indigo-600 hover:text-indigo-800 transition-colors font-semibold flex items-center gap-2">
            <ArrowLeft size={16} /> Voltar para o início
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-black text-gray-900">Entre em Contato</h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Tem dúvidas, sugestões ou precisa de suporte técnico? Nossa equipe está pronta para ajudar.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
          {/* Informações de contato */}
          <div className="space-y-8">
            <h2 className="text-2xl font-bold mb-6">Informações Diretas</h2>
            
            <div className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center shrink-0">
                <Mail size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">E-mail</h3>
                <p className="text-gray-500 mb-2">Para suporte e dúvidas comerciais, envie-nos um e-mail.</p>
                <a href="mailto:contato@odontoassistai.com.br" className="text-indigo-600 font-medium hover:underline text-lg">
                  contato@odontoassistai.com.br
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-green-50 text-green-600 rounded-xl flex items-center justify-center shrink-0">
                <Phone size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-1">WhatsApp</h3>
                <p className="text-gray-500 mb-2">Fale conosco rapidamente pelo WhatsApp.</p>
                <a href="https://wa.me/558132042969" target="_blank" rel="noopener noreferrer" className="text-green-600 font-medium hover:underline text-lg">
                  (81) 3204-2969
                </a>
              </div>
            </div>
          </div>

          {/* Formulário */}
          <div className="bg-white p-8 rounded-3xl shadow-xl shadow-indigo-900/5 border border-indigo-50">
            <h2 className="text-2xl font-bold mb-6">Envie uma Mensagem</h2>
            
            {sent ? (
              <div className="bg-green-50 text-green-700 p-6 rounded-2xl border border-green-200 text-center space-y-4 animate-fade-in">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto text-green-600">
                  <Send size={32} />
                </div>
                <h3 className="text-xl font-bold">Mensagem enviada com sucesso!</h3>
                <p>Nossa equipe retornará o contato o mais breve possível.</p>
                <button 
                  onClick={() => setSent(false)}
                  className="mt-4 px-6 py-2 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition"
                >
                  Enviar outra mensagem
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Seu Nome</label>
                  <input 
                    type="text" 
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 focus:outline-none transition-all"
                    placeholder="Ex: João da Silva"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Seu E-mail</label>
                  <input 
                    type="email" 
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 focus:outline-none transition-all"
                    placeholder="Ex: joao@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Como podemos ajudar?</label>
                  <textarea 
                    required
                    rows="4"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 focus:outline-none transition-all resize-none"
                    placeholder="Descreva sua dúvida, sugestão ou necessidade..."
                  ></textarea>
                </div>
                <button 
                  type="submit"
                  disabled={loading}
                  className={`w-full py-4 text-white font-bold rounded-xl transition-colors shadow-lg shadow-indigo-200 flex items-center justify-center gap-2 ${loading ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 hover:-translate-y-0.5'}`}
                >
                  <Send size={18} /> {loading ? "Enviando..." : "Enviar Mensagem"}
                </button>
                {errorMsg && (
                  <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm font-semibold text-center mt-4 border border-red-200">
                    {errorMsg}
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
